import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { 
  getQuestionsForRelationshipType, 
  getDimensionsForRelationshipType,
  ASSESSMENT_QUESTIONS 
} from '@/data/relationshipAssessmentQuestions';

export interface AssessmentResponse {
  questionId: string;
  score: number; // 1-7
}

export interface SavedAssessment {
  id: string;
  user_id: string;
  relationship_type_id: string;
  assessment_date: string;
  connection_quality: number | null;
  communication_quality: number | null;
  trust_level: number | null;
  support_received: number | null;
  support_given: number | null;
  conflict_resolution: number | null;
  emotional_safety: number | null;
  authenticity: number | null;
  overall_satisfaction: number | null;
  relationship_health_score: number | null;
  notes: string | null;
  challenges: string | null;
  growth_areas: string | null;
  created_at: string;
  updated_at: string;
}

export interface DimensionScore {
  dimension: string;
  name: string;
  score: number;
  maxScore: number;
  percentage: number;
}

export function useRelationshipAssessment(relationshipTypeIdOrPartnerId?: string) {
  const [assessments, setAssessments] = useState<SavedAssessment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  // Check if the param is a user UUID (for partner viewing) or a type ID
  const isPartnerQuery = relationshipTypeIdOrPartnerId?.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i) 
    && !relationshipTypeIdOrPartnerId?.startsWith('00000000');

  const fetchAssessments = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // If fetching partner's assessments
      const targetUserId = isPartnerQuery ? relationshipTypeIdOrPartnerId : user.id;

      let query = supabase
        .from('relationship_assessments')
        .select('*')
        .eq('user_id', targetUserId)
        .order('assessment_date', { ascending: false });

      // If not a partner query, filter by relationship type if provided
      if (!isPartnerQuery && relationshipTypeIdOrPartnerId) {
        query = query.eq('relationship_type_id', relationshipTypeIdOrPartnerId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setAssessments(data || []);
    } catch (error) {
      console.error('Error fetching assessments:', error);
    } finally {
      setIsLoading(false);
    }
  }, [relationshipTypeIdOrPartnerId, isPartnerQuery]);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  const saveAssessment = async (
    responses: AssessmentResponse[],
    relationshipType: 'romantic' | 'family' | 'friendship' | 'work' | 'other',
    notes?: string
  ) => {
    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Calculate dimension scores
      const dimensionScores = calculateDimensionScores(responses, relationshipType);
      
      // Map to existing column structure
      const connectionScore = dimensionScores.find(d => d.dimension === 'connection')?.score || 0;
      const communicationScore = dimensionScores.find(d => d.dimension === 'communication')?.score || 0;
      const securityScore = dimensionScores.find(d => d.dimension === 'security')?.score || 0;
      const teamworkScore = dimensionScores.find(d => d.dimension === 'teamwork')?.score || 0;
      const autonomyScore = dimensionScores.find(d => d.dimension === 'autonomy')?.score || 0;
      const growthScore = dimensionScores.find(d => d.dimension === 'growth')?.score || 0;
      
      // Calculate overall health score (average of all dimensions, scaled to 100)
      const totalScore = dimensionScores.reduce((acc, d) => acc + d.score, 0);
      const totalMax = dimensionScores.reduce((acc, d) => acc + d.maxScore, 0);
      const healthScore = totalMax > 0 ? (totalScore / totalMax) * 100 : 0;

      // We need a relationship_type_id - use a placeholder UUID based on type
      // In a real app, this would come from a relationship_types table
      const typeIdMap: Record<string, string> = {
        romantic: '00000000-0000-0000-0000-000000000001',
        family: '00000000-0000-0000-0000-000000000002',
        friendship: '00000000-0000-0000-0000-000000000003',
        work: '00000000-0000-0000-0000-000000000004',
        other: '00000000-0000-0000-0000-000000000005'
      };

      // Identify challenges and growth areas
      const challenges = dimensionScores
        .filter(d => d.percentage < 60)
        .map(d => d.name)
        .join(', ') || null;
      
      const growthAreas = dimensionScores
        .filter(d => d.percentage >= 60 && d.percentage < 80)
        .map(d => d.name)
        .join(', ') || null;

      const { data, error } = await supabase
        .from('relationship_assessments')
        .insert({
          user_id: user.id,
          relationship_type_id: typeIdMap[relationshipType],
          assessment_date: new Date().toISOString().split('T')[0],
          connection_quality: Math.round(connectionScore / 3), // Scale 3-question to 1-7
          communication_quality: Math.round(communicationScore / 3),
          trust_level: Math.round(securityScore / 3),
          support_received: Math.round(teamworkScore / 3),
          support_given: Math.round(teamworkScore / 3),
          conflict_resolution: Math.round(communicationScore / 3),
          emotional_safety: Math.round(securityScore / 3),
          authenticity: Math.round(autonomyScore / 3),
          overall_satisfaction: Math.round(growthScore / 3),
          notes,
          challenges,
          growth_areas: growthAreas
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Assessment saved",
        description: "Your relationship snapshot has been recorded."
      });

      await fetchAssessments();
      return data;
    } catch (error) {
      console.error('Error saving assessment:', error);
      toast({
        title: "Error saving assessment",
        description: "Please try again.",
        variant: "destructive"
      });
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const calculateDimensionScores = (
    responses: AssessmentResponse[],
    relationshipType: 'romantic' | 'family' | 'friendship' | 'work' | 'other'
  ): DimensionScore[] => {
    const dimensions = getDimensionsForRelationshipType(relationshipType);
    const questions = getQuestionsForRelationshipType(relationshipType);
    
    return dimensions.map(dim => {
      const dimQuestions = questions.filter(q => q.dimension === dim.id);
      const dimResponses = responses.filter(r => 
        dimQuestions.some(q => q.id === r.questionId)
      );
      
      const score = dimResponses.reduce((acc, r) => acc + r.score, 0);
      const maxScore = dimQuestions.length * 7;
      
      return {
        dimension: dim.id,
        name: dim.name,
        score,
        maxScore,
        percentage: maxScore > 0 ? (score / maxScore) * 100 : 0
      };
    });
  };

  const getLatestAssessment = () => {
    return assessments[0] || null;
  };

  const compareAssessments = (assessment1: SavedAssessment, assessment2: SavedAssessment) => {
    const fields = [
      'connection_quality',
      'communication_quality', 
      'trust_level',
      'support_received',
      'conflict_resolution',
      'emotional_safety',
      'authenticity',
      'overall_satisfaction'
    ] as const;

    return fields.map(field => ({
      field,
      before: assessment1[field] || 0,
      after: assessment2[field] || 0,
      change: (assessment2[field] || 0) - (assessment1[field] || 0)
    }));
  };

  return {
    assessments,
    isLoading,
    isSaving,
    saveAssessment,
    calculateDimensionScores,
    getLatestAssessment,
    compareAssessments,
    refetch: fetchAssessments
  };
}
